import React, { FC, useEffect, useLayoutEffect, useMemo } from 'react';
import {
  Box,
  CircularProgress,
  createStyles,
  Grid,
  List,
  ListSubheader,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import CheckBoxListItem from './CheckBoxListItem/CheckBoxListItem';
import { groupByEchelon } from '../../../../utils/entity';
import { capitalizeFirstLetter } from '../../../../utils/common';
import { IEntity, IUserEditState } from '../../AdditionalInfos/utils';
import { useLazyGetAllEntities } from '../../../../hooks/queries/useEntity';
import { EchelonName, Role } from '../../../../common/constants';
import { useGetAllEchelons } from '../../../../hooks/queries';
import CustomAccordion from '../../../../common/components/CustomAccordion/CustomAccordion';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      flexDirection: 'column',
    },
    subtitle: {
      textAlign: 'center',
      color: '#9EA3B0',
      fontSize: 16,
      fontStyle: 'italic',
      margin: 20,
    },
    list: {
      width: '100%',
    },
    listSubHeader: {
      height: 15,
      color: 'black',
      paddingLeft: 0,
    },
    entityContainer: {
      marginTop: 20,
    },
    loading: {
      flexGrow: 1,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: 100,
    },
  }),
);

interface Props {
  userId: number;
  showSubTitle?: boolean;
  canEditUser: boolean;
  userState: IUserEditState;
  setUserState: React.Dispatch<React.SetStateAction<IUserEditState>>;
}

const EntityManagingList: FC<Props> = (props) => {
  const { showSubTitle, canEditUser, setUserState, userState } = props;

  const classes = useStyles();

  const {
    data: EchelonData,
    loading: loadingAllEchelons,
  } = useGetAllEchelons();

  const reseauEchelon = EchelonData?.getAllEchelons?.find(
    (echelon) => echelon?.name === EchelonName?.RESEAU,
  );

  const [
    getEntities,
    { data, loading: loadingEntities },
  ] = useLazyGetAllEntities({
    where: { echelon: { priority: { equals: reseauEchelon?.priority } } },
  });

  const handleManageEntity = (
    event: React.ChangeEvent<HTMLInputElement>,
    entityId: number,
  ) => {
    setUserState((prev) => {
      return {
        ...prev,
        userEnseigne: {
          id: prev.userEnseigne?.id || 0,
          name: prev.userEnseigne?.name || '',
          entities:
            prev.userEnseigne?.entities?.map((entity) => {
              if (entity.id === entityId) {
                return {
                  ...entity,
                  managed: event.target.checked,
                };
              }
              return entity;
            }) || [],
        },
      };
    });
  };

  useLayoutEffect(() => {
    getEntities();
  }, []);

  useEffect(() => {
    if (!userState?.roles.includes(Role.MANDATARY)) {
      const reseauEntities: IEntity[] =
        data?.allEntities?.entities?.map((entity) => ({
          id: entity?.id ?? -1,
          name: entity?.name || '',
          managed: entity?.managers?.some(
            (manager) => manager.id === userState?.userId,
          ),
          echelon: {
            id: entity?.echelon.id ?? -1,
            name: entity?.echelon.name,
            priority: entity?.echelon.priority,
          },
        })) || [];
      setUserState((prevState) => ({
        ...prevState,
        userEnseigne: {
          ...(prevState?.userEnseigne as any),
          entities: [
            ...(prevState?.userEnseigne?.entities || []),
            ...reseauEntities,
          ],
        },
      }));
    }
  }, [data]);

  const entityListByEchelon = useMemo(
    () => groupByEchelon(userState.userEnseigne?.entities || []),
    [userState],
  );

  const userName = `${userState?.firstName} ${
    userState?.lastName ? userState.lastName : ''
  }`;

  if (loadingAllEchelons || loadingEntities)
    return (
      <Container>
        <Box className={classes.loading}>
          <CircularProgress size={20} />
        </Box>
      </Container>
    );

  if (!entityListByEchelon?.length && !loadingEntities)
    return (
      <Container>
        <Grid item xs={12}>
          <Typography className={classes.subtitle}>
            Aucune entité trouvée dans l'enseigne{' '}
            {userState.userEnseigne?.name || ''}.
          </Typography>
        </Grid>
      </Container>
    );

  return (
    <Container>
      {showSubTitle && (
        <Grid item xs={12}>
          <Typography className={classes.subtitle}>
            Choisissez les entités sur lesquelles l'utilisateur {userName} aura
            une vue statistique.
          </Typography>
        </Grid>
      )}
      <Grid item xs={12}>
        {entityListByEchelon?.map((_echelon: any, index: any) => (
          <List
            key={`echelon-list-${index}`}
            subheader={
              <ListSubheader component="div" className={classes.listSubHeader}>
                {`${capitalizeFirstLetter(_echelon?.name)} :`}
              </ListSubheader>
            }
            className={classes.list}>
            <Box className={classes.entityContainer}>
              {_echelon?.entities?.map((entity: any) => (
                <CheckBoxListItem
                  key={`entity-list-${entity.id}`}
                  id={entity.id}
                  checked={!!entity.managed}
                  text={entity.name || ''}
                  handleChange={handleManageEntity}
                  disabled={!canEditUser}
                />
              ))}
            </Box>
          </List>
        ))}
      </Grid>
    </Container>
  );
};

const Container: FC = ({ children }) => (
  <CustomAccordion title="Entités supervisées ou monitorées">
    <Grid container>{children} </Grid>
  </CustomAccordion>
);

export default EntityManagingList;
